import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { API_BASE_URL, AUTH_TOKEN_KEY, REFRESH_TOKEN_KEY, USER_KEY } from '../utils/constants';
import { ApiError } from '../utils/types';
import { setAuthFailed, hasAuthFailed, clearAuthFailed as clearAuthFailedState } from './authFailedState';
import { getActiveMemberId } from './subjectState';

/**
 * Generate a unique request ID for correlation
 */
const generateRequestId = (): string => {
  return `${Date.now().toString(36)}-${Math.random().toString(36).substring(2, 9)}`;
};

// Create axios instance
const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30 seconds
  withCredentials: true, // Enable sending cookies with requests
});

// Request interceptor - add request ID for correlation
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Add request-id to all requests for correlation
    const requestId = generateRequestId();
    config.headers['X-Request-ID'] = requestId;
    // Cookies are sent automatically with withCredentials: true
    // No need to manually add Authorization header

    // Family health: when a member (other than self) is selected in the Health
    // page, transparently scope health/oura/whoop calls to that subject. Account
    // /OAuth ops ignore member_id server-side, so blanket injection is safe.
    const memberId = getActiveMemberId();
    const url = config.url || '';
    if (memberId && /\/api\/v1\/(health|oura|whoop)\b/.test(url)) {
      if (config.params && config.params.member_id === undefined) {
        config.params.member_id = memberId;
      } else if (!config.params) {
        config.params = { member_id: memberId };
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Track if we're currently refreshing to avoid multiple refresh attempts
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: unknown) => void;
  reject: (reason?: unknown) => void;
}> = [];

// Circuit breaker: Track refresh failures to prevent infinite loops (especially on iOS)
let refreshFailureCount = 0;
let lastRefreshFailureTime = 0;
const MAX_REFRESH_FAILURES = 5;
const REFRESH_FAILURE_RESET_MS = 120000; // Reset counter after 2 minutes of no failures

// Flag to prevent multiple simultaneous redirects to login
let isRedirectingToLogin = false;

// Auth logout diagnostics. redirectToLogin() does a hard page reload, which
// wipes the console before you can read why you were logged out. We persist the
// reason to sessionStorage (survives the reload, cleared when the tab closes) so
// it can be inspected after the fact via window.authDebug() in the console.
const AUTH_DEBUG_KEY = 'concerto_auth_debug';

const recordAuthDebug = (reason: string, extra: Record<string, unknown> = {}): void => {
  try {
    const entry = { reason, ...extra, path: window.location.pathname, at: new Date().toISOString() };
    const prev = JSON.parse(sessionStorage.getItem(AUTH_DEBUG_KEY) || '[]');
    prev.push(entry);
    // Keep only the last 10 events to bound storage
    sessionStorage.setItem(AUTH_DEBUG_KEY, JSON.stringify(prev.slice(-10)));
    console.error('[Auth] logout diagnostic recorded (run authDebug() to view):', entry);
  } catch {
    // sessionStorage unavailable (private mode edge cases) - best effort only
  }
};

// Console query helper: run `authDebug()` in devtools to see recent logout causes.
if (typeof window !== 'undefined') {
  (window as unknown as { authDebug: () => unknown }).authDebug = () => {
    try {
      const rows = JSON.parse(sessionStorage.getItem(AUTH_DEBUG_KEY) || '[]');
      // eslint-disable-next-line no-console
      console.table(rows);
      return rows;
    } catch {
      return [];
    }
  };
}

/**
 * Clear auth failed state. Called on successful login.
 * Exported so auth.ts can call it after successful login.
 * Wraps the authFailedState module and also resets module-level flags.
 */
export const clearAuthFailed = async (): Promise<void> => {
  await clearAuthFailedState();
  // Also reset module-level flags
  isRedirectingToLogin = false;
  refreshFailureCount = 0;
};

const processQueue = (error: unknown, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

/**
 * Redirect to login page, clearing auth state.
 *
 * CRITICAL: This function sets terminal state SYNCHRONOUSLY before any async
 * operations to prevent race conditions on iOS where React re-renders before
 * async storage operations complete.
 *
 * Order of operations:
 * 1. Check if already redirecting (prevent duplicates)
 * 2. Set terminal auth_failed state (SYNC - immediate effect)
 * 3. Clear localStorage (SYNC - immediate effect for isAuthenticated())
 * 4. Clear async storage (for iOS native - best effort)
 * 5. Redirect to login
 */
const redirectToLogin = async (diag?: { reason: string;[k: string]: unknown }) => {
  // Record why we're logging out BEFORE the dedupe guard, so every trigger is
  // captured even if a redirect is already in flight.
  if (diag) {
    recordAuthDebug(diag.reason, diag);
  }

  // Check both flags to prevent duplicate redirects
  if (isRedirectingToLogin || hasAuthFailed()) {
    console.log('[Auth] Already redirecting to login or auth failed, skipping');
    return;
  }

  isRedirectingToLogin = true;

  // STEP 1: Set terminal auth_failed state FIRST (synchronous)
  // This ensures isAuthenticated() returns false immediately
  setAuthFailed();

  // STEP 2: Clear localStorage SYNCHRONOUSLY
  // This ensures ProtectedRoute sees the user as logged out immediately
  // even before the redirect completes
  console.log('[Auth] Clearing auth state synchronously...');
  localStorage.removeItem(USER_KEY);
  localStorage.removeItem(AUTH_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);

  // STEP 3: Clear Capacitor Preferences (iOS native) and AWAIT completion
  // CRITICAL: We must await this before any hard redirect, otherwise
  // storage.initialize() on reload will re-sync stale user data from
  // Preferences back into localStorage, causing a login page refresh loop.
  try {
    const { storage } = await import('../utils/storage');
    await Promise.all([
      storage.remove(USER_KEY).catch(() => {}),
      storage.remove(AUTH_TOKEN_KEY).catch(() => {}),
      storage.remove(REFRESH_TOKEN_KEY).catch(() => {}),
    ]);
  } catch (e) {
    // Ignore - localStorage already cleared synchronously
  }

  // STEP 4: Only do a hard redirect if NOT already on the login page.
  // If we're already on /login, a hard reload would clear the user's
  // in-progress form input (email/password they're typing).
  if (window.location.pathname === '/login') {
    console.log('[Auth] Already on login page, skipping hard redirect');
    isRedirectingToLogin = false;
    return;
  }

  console.log('[Auth] Redirecting to login...');
  // Use replace to prevent back button from returning to authenticated page
  window.location.replace('/login');
};

/**
 * Check if we should attempt a token refresh based on circuit breaker state.
 * Returns false if we've had too many recent failures (prevents infinite loops on iOS).
 */
const shouldAttemptRefresh = (): boolean => {
  const now = Date.now();

  // Reset failure count if enough time has passed
  if (now - lastRefreshFailureTime > REFRESH_FAILURE_RESET_MS) {
    refreshFailureCount = 0;
  }

  if (refreshFailureCount >= MAX_REFRESH_FAILURES) {
    return false;
  }

  return true;
};

/**
 * Record a refresh failure and determine if we should give up.
 * Only counts auth failures, not network errors.
 * @param error - The error from the refresh attempt
 * @returns true if we've exceeded the threshold and should logout
 */
const recordRefreshFailure = (error: unknown): boolean => {
  const now = Date.now();

  // Reset counter if enough time has passed
  if (now - lastRefreshFailureTime > REFRESH_FAILURE_RESET_MS) {
    refreshFailureCount = 0;
  }

  // Check if this is a network error (no response) vs auth error
  const isNetworkError = error instanceof Error &&
    (error.message.includes('Network Error') ||
     error.message.includes('timeout') ||
     !('response' in (error as unknown as Record<string, unknown>)));

  // Only count auth errors (401s) toward the circuit breaker
  if (isNetworkError) {
    return false;
  }

  refreshFailureCount++;
  lastRefreshFailureTime = now;

  return refreshFailureCount >= MAX_REFRESH_FAILURES;
};

// Response interceptor - handle errors and token refresh
api.interceptors.response.use(
  (response) => {
    // Reset circuit breaker on successful response (user is authenticated)
    refreshFailureCount = 0;
    return response;
  },
  async (error: AxiosError<ApiError>) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // Handle 401 Unauthorized - try to refresh token
    if (error.response?.status === 401 && !originalRequest._retry) {
      // SHORT-CIRCUIT: If auth has permanently failed, reject immediately
      // This is the primary defense against infinite loops on iOS
      if (hasAuthFailed()) {
        return Promise.reject(error);
      }

      // SHORT-CIRCUIT: If we were never authenticated (no user in storage), the
      // 401 is expected — e.g. a globally-mounted provider polling on /login.
      // Attempting a refresh here burns the refresh cookie (or trips
      // TOKEN_EXPIRED with no cookie at all) and poisons subsequent logins.
      if (!localStorage.getItem(USER_KEY)) {
        return Promise.reject(error);
      }

      // Check if this is the refresh endpoint itself failing - don't retry refresh on refresh
      const isRefreshEndpoint = originalRequest.url?.includes('/auth/refresh-token');
      if (isRefreshEndpoint) {
        recordRefreshFailure(error);
        await redirectToLogin({ reason: 'refresh_endpoint_401', url: originalRequest.url, status: 401 });
        return Promise.reject(error);
      }

      // Circuit breaker: Don't attempt refresh if we've failed too many times recently
      if (!shouldAttemptRefresh()) {
        await redirectToLogin({ reason: 'circuit_breaker', url: originalRequest.url, failures: refreshFailureCount });
        return Promise.reject(error);
      }

      if (isRefreshing) {
        // If already refreshing, queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then(() => {
            // Cookies are automatically sent, just retry the request
            return api(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        // Calculate backoff delay (exponential: 1s, 2s, 4s, 8s, 16s)
        const backoffDelay = Math.min(1000 * Math.pow(2, refreshFailureCount), 16000);
        if (refreshFailureCount > 0) {
          console.log(`[Auth] Waiting ${backoffDelay}ms before refresh attempt`);
          await new Promise(resolve => setTimeout(resolve, backoffDelay));
        }

        // Attempt to refresh the token using httpOnly cookies
        const refreshResponse = await axios.post(
          `${API_BASE_URL}/api/v1/auth/refresh-token`,
          {},
          {
            withCredentials: true, // Send refresh token cookie
            timeout: 10000, // 10 second timeout for refresh
          }
        );

        console.log('[Auth] Token refreshed successfully, response status:', refreshResponse.status);

        // Reset circuit breaker on success
        refreshFailureCount = 0;

        // Process queued requests
        processQueue(null, null);
        isRefreshing = false;

        // Retry the original request (new access token cookie will be sent automatically)
        return api(originalRequest);
      } catch (refreshError) {
        // Refresh failed, clear auth state and redirect to login
        let errorCode = 'UNKNOWN';
        let errorStatus: number | string = 'NO_RESPONSE';
        let errorMessage = 'Unknown error';
        let errorData: unknown = undefined;

        if (axios.isAxiosError<ApiError>(refreshError)) {
          errorCode = refreshError.response?.data?.code || refreshError.response?.data?.error || 'UNKNOWN';
          errorStatus = refreshError.response?.status || 'NO_RESPONSE';
          errorMessage = refreshError.response?.data?.message || refreshError.message;
          errorData = refreshError.response?.data;
        } else if (refreshError instanceof Error) {
          errorMessage = refreshError.message;
        }

        console.error(`[Auth] Token refresh failed: status=${errorStatus}, code=${errorCode}, message=${errorMessage}`);
        console.error('[Auth] Full refresh error:', JSON.stringify({
          status: errorStatus,
          data: errorData,
          message: errorMessage,
        }));

        recordRefreshFailure(refreshError);
        processQueue(refreshError, null);
        isRefreshing = false;

        await redirectToLogin({
          reason: 'refresh_failed',
          url: originalRequest.url,
          status: errorStatus,
          code: errorCode,
          message: errorMessage,
        });
        return Promise.reject(refreshError);
      }
    }

    // Format error message
    const errorMessage = error.response?.data?.message ||
                        error.response?.data?.error ||
                        error.message ||
                        'An unexpected error occurred';

    // Extract all relevant data from the error response
    const errorData: Partial<ApiError> = error.response?.data || {};

    return Promise.reject({
      message: errorMessage,
      status: error.response?.status,
      details: {
        ...errorData,
        upgrade_required: errorData.upgrade_required,
        limit: errorData.limit,
        current_count: errorData.current_count,
      },
    });
  }
);

export default api;

